'use client';
import { useEffect, useState } from 'react';
 import Link from'next/link';
 import Header from'@/components/common/Header';
 import Footer from'@/components/common/Footer';
 import Icon from'@/components/ui/AppIcon';

const architectureLayers = [
  {
    id: 'layer_presentation',
    name: 'Presentation Layer',
    description: 'User interfaces and experience',
    technologies: ['SAP Fiori', 'UI5', 'Mobile Apps', 'SAP Analytics Cloud'],
    responsibilities: [
      'End-user interaction',
      'Responsive design',
      'Role-based UX',
      'Analytics visualization',
    ],
  },
  {
    id: 'layer_application',
    name: 'Application Layer',
    description: 'Business logic and processes',
    technologies: ['ABAP', 'Business Objects', 'Process Orchestration', 'Workflow'],
    responsibilities: [
      'Business process execution',
      'Transaction processing',
      'Business rules',
      'Process automation',
    ],
  },
  {
    id: 'layer_integration',
    name: 'Integration Layer',
    description: 'Connectivity and data exchange',
    technologies: ['SAP BTP', 'APIs (REST/OData)', 'RFC', 'IDocs', 'Event Mesh'],
    responsibilities: [
      'System integration',
      'API management',
      'Event-driven architecture',
      'Data synchronization',
    ],
  },
  {
    id: 'layer_data',
    name: 'Data Layer',
    description: 'Persistence and processing',
    technologies: ['SAP HANA', 'Core Data Services (CDS)', 'Data Warehouse Cloud'],
    responsibilities: [
      'In-memory processing',
      'Transactional data',
      'Master data',
      'Analytics foundation',
    ],
  },
]

const functionalModules = [
  { id: 'mod_finance', name: 'Finance (FI/CO)', category: 'Core', color: 'bg-blue-900/20 border-blue-600/30' },
  { id: 'mod_scm', name: 'Supply Chain (MM/SD)', category: 'Core', color: 'bg-green-900/20 border-green-600/30' },
  { id: 'mod_manufacturing', name: 'Manufacturing (PP/QM)', category: 'Core', color: 'bg-purple-900/20 border-purple-600/30' },
  { id: 'mod_hr', name: 'Human Resources', category: 'Core', color: 'bg-orange-900/20 border-orange-600/30' },
  { id: 'mod_sales', name: 'Sales & Distribution', category: 'Core', color: 'bg-pink-900/20 border-pink-600/30' },
  { id: 'mod_procurement', name: 'Procurement', category: 'Core', color: 'bg-teal-900/20 border-teal-600/30' },
  { id: 'mod_asset', name: 'Asset Management', category: 'Extended', color: 'bg-indigo-900/20 border-indigo-600/30' },
  { id: 'mod_project', name: 'Project Systems', category: 'Extended', color: 'bg-yellow-900/20 border-yellow-600/30' },
  { id: 'mod_customer', name: 'Customer Service', category: 'Extended', color: 'bg-red-900/20 border-red-600/30' },
]

const integrationPoints = [
  {
    id: 'int_api',
    type: 'APIs',
    protocols: ['REST', 'OData', 'SOAP'],
    useCase: 'External system integration, mobile apps, third-party services',
  },
  {
    id: 'int_rfc',
    type: 'RFC',
    protocols: ['Remote Function Call'],
    useCase: 'Legacy SAP system communication, real-time data exchange',
  },
  {
    id: 'int_idoc',
    type: 'IDocs',
    protocols: ['Intermediate Document'],
    useCase: 'Asynchronous data transfer, batch processing',
  },
  {
    id: 'int_btp',
    type: 'SAP BTP',
    protocols: ['Integration Suite', 'Event Mesh'],
    useCase: 'Cloud extensions, event-driven architecture, microservices',
  },
  {
    id: 'int_cds',
    type: 'CDS Views',
    protocols: ['Core Data Services'],
    useCase: 'Data modeling, virtual data models, analytics',
  },
]

export default function LandscapeMapPage() {
  const [expandedLayer, setExpandedLayer] = useState<string | null>(null)

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal')
    if (!reveals.length) return

    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    )
    reveals.forEach(el => observer.observe(el))

    return () => observer.disconnect()
  }, [])

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />

      <main className="pt-24">
        {/* Page Header */}
        <section className="mx-auto max-w-7xl px-6 py-16 border-b border-border">
          <div className="reveal">
            <h1 className="font-serif text-display-md md:text-display-lg font-bold mb-4">
              S/4 Landscape Map
            </h1>
            <p className="font-mono text-lg text-muted-foreground max-w-3xl">
              The complete architecture - modules, layers, integration points. A structural view of the entire S/4HANA landscape.
            </p>
          </div>
        </section>

        {/* Architecture Layers */}
        <section className="mx-auto max-w-7xl px-6 py-16 border-b border-border">
          <div className="mb-10 reveal">
            <h2 className="font-serif text-3xl font-semibold mb-2">Architecture Layers</h2>
            <p className="font-mono text-sm text-muted-foreground">Vertical structure of S/4HANA</p>
          </div>

          <div className="space-y-4">
            {architectureLayers.map((layer) => (
              <div key={layer.id} className="border border-border rounded reveal">
                <button
                  onClick={() => setExpandedLayer(expandedLayer === layer.id ? null : layer.id)}
                  className="w-full p-6 text-left hover:bg-muted transition-colors flex items-center justify-between"
                >
                  <div className="flex-1">
                    <h3 className="font-serif text-xl font-semibold mb-1">{layer.name}</h3>
                    <p className="font-mono text-sm text-muted-foreground">{layer.description}</p>
                  </div>
                  <Icon
                    name="ChevronDownIcon"
                    size={20}
                    className={`text-muted-foreground transition-transform ${
                      expandedLayer === layer.id ? 'rotate-180' : ''
                    }`}
                    variant="outline"
                  />
                </button>

                {expandedLayer === layer.id && (
                  <div className="px-6 pb-6 border-t border-border">
                    <div className="grid md:grid-cols-2 gap-6 pt-6">
                      <div>
                        <h4 className="font-mono text-xs uppercase text-muted-foreground mb-3">Technologies</h4>
                        <ul className="space-y-2">
                          {layer.technologies.map((tech, idx) => (
                            <li key={idx} className="font-mono text-sm flex items-center gap-2">
                              <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                              {tech}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-mono text-xs uppercase text-muted-foreground mb-3">Key Responsibilities</h4>
                        <ul className="space-y-2">
                          {layer.responsibilities.map((resp, idx) => (
                            <li key={idx} className="font-mono text-sm flex items-center gap-2">
                              <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full"></span>
                              {resp}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Functional Modules */}
        <section className="mx-auto max-w-7xl px-6 py-16 border-b border-border">
          <div className="mb-10 reveal">
            <h2 className="font-serif text-3xl font-semibold mb-2">Functional Modules</h2>
            <p className="font-mono text-sm text-muted-foreground">Horizontal breadth - business capabilities</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {functionalModules.map((module) => (
              <div
                key={module.id}
                className={`p-6 border rounded ${module.color} reveal`}
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-mono font-semibold">{module.name}</h3>
                  <span className="text-xs font-mono px-2 py-0.5 bg-background/50 rounded">
                    {module.category}
                  </span>
                </div>
                <Link
                  href="/functional-playbook"
                  className="inline-flex items-center gap-1 text-xs font-mono text-primary hover:underline mt-2"
                >
                  View details
                  <Icon name="ArrowRightIcon" size={12} variant="outline" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        {/* Integration Points */}
        <section className="mx-auto max-w-7xl px-6 py-16">
          <div className="mb-10 reveal">
            <h2 className="font-serif text-3xl font-semibold mb-2">Integration Points</h2>
            <p className="font-mono text-sm text-muted-foreground">Connectivity patterns and protocols</p>
          </div>

          <div className="overflow-x-auto reveal">
            <table className="table-research">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Protocols</th>
                  <th>Primary Use Case</th>
                </tr>
              </thead>
              <tbody>
                {integrationPoints.map((point) => (
                  <tr key={point.id}>
                    <td className="font-mono font-semibold">{point.type}</td>
                    <td className="font-mono text-sm">{point.protocols.join(', ')}</td>
                    <td className="font-mono text-sm text-muted-foreground">{point.useCase}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Visual Diagram Placeholder */}
          <div className="mt-10 p-8 bg-card border border-border rounded reveal">
            <p className="font-mono text-xs text-muted-foreground mb-4 text-center">Integration Flow Diagram</p>
            <pre className="font-mono text-xs text-muted-foreground overflow-x-auto">
{`┌─────────────┐      API/OData      ┌──────────────┐
│  External   │◄───────────────────►│  S/4HANA     │
│  Systems    │                     │  Application │
└─────────────┘                     └──────────────┘
                                            │
                    RFC                     │ CDS Views
┌─────────────┐◄───────────────────────────┤
│  Legacy SAP │                             │
│  Systems    │                             ▼
└─────────────┘                     ┌──────────────┐
                                    │  SAP HANA    │
┌─────────────┐      IDocs          │  Database    │
│  Third-Party│◄────────────────────┤              │
│  Apps       │                     └──────────────┘
└─────────────┘                             │
                                            │
                    Event Mesh              │
┌─────────────┐◄───────────────────────────┘
│  SAP BTP    │
│  Extensions │
└─────────────┘`}
            </pre>
          </div>
        </section>

        {/* Navigation CTA */}
        <section className="mx-auto max-w-7xl px-6 py-16 border-t border-border">
          <div className="text-center space-y-6 reveal">
            <p className="font-mono text-muted-foreground">
              Dive into functional or technical details
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                href="/functional-playbook"
                className="px-6 py-3 bg-primary text-primary-foreground font-mono text-sm rounded hover:opacity-90 transition-opacity"
              >
                Functional Playbook
              </Link>
              <Link
                href="/technical-playbook"
                className="px-6 py-3 border border-border font-mono text-sm rounded hover:bg-muted transition-colors"
              >
                Technical Playbook
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}